/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Site, SiteChangeRequest } from '../types/Site';
import AdminUserDelete from './AdminUserDelete';
import { updateSiteInDatabase, fetchSiteChangeRequests, updateSiteChangeRequestStatus, checkSiteInAllTables, fetchSignalBoxChangeRequests, updateSignalBoxChangeRequestStatus, applySignalBoxChange, fetchAllSignalBoxes, deleteSignalBox, fetchFibreChangeRequests, updateFibreChangeRequestStatus, applyFibreChange, insertNewFibreSite, fetchSptCircuitChangeRequests, updateSptCircuitChangeRequestStatus, applySptCircuitChange, SptCircuitChangeRequest } from '../utils/siteUtils';
import { SignalBoxChangeRequest, FibreChangeRequest } from '../types/Site';

interface AdminPanelProps {
  onClose: () => void;
  sites: Site[];
  onUpdateSites: (newSites: Site[], skipJsonDownload?: boolean) => void;
}

interface UserProfile {
  id: string;
  email: string;
  is_admin: boolean;
  is_approved: boolean;
  created_at: string;
}

const AdminPanelContainer = styled.div<{ isDragging: boolean; posX: number; posY: number; }>`
  position: fixed;
  top: ${props => props.posY}px;
  left: ${props => props.posX}px;
  background: rgba(20, 20, 20, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: #f5f5f5;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 1000;
  width: 600px;
  max-width: 95vw;
  height: 75vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  cursor: ${props => props.isDragging ? 'grabbing' : 'default'};
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: ${props => props.isDragging 
      ? '0 28px 72px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08)' 
      : '0 24px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08)'};
  }

  @media (max-width: 768px) {
    width: 95%;
    height: 85vh;
    padding: 16px;
  }

  @media (max-width: 640px) {
    position: fixed;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%);
    width: 95%;
    height: 90vh;
    padding: 14px;
    border-radius: 16px;
    max-width: none;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  cursor: grab;
  flex-shrink: 0;
  
  &:active {
    cursor: grabbing;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: white;
  
  @media (max-width: 640px) {
    font-size: 1.1rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 6px 10px;
  transition: all 0.2s;
  line-height: 1;
  
  &:hover {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
    border-color: rgba(255, 107, 107, 0.2);
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
`;

const Button = styled.button`
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 8px 14px;
  margin-right: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.02);
    color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.04);
    cursor: not-allowed;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const StatValue = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: #4ade80;
`;

const StatLabel = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  overflow-x: auto;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  user-select: none;
  touch-action: pan-x;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const Tab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(74, 222, 128, 0.12)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${props => props.active ? 'rgba(74, 222, 128, 0.3)' : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 20px;
  color: ${props => props.active ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'};
  padding: 7px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    color: ${props => props.active ? '#4ade80' : 'rgba(255, 255, 255, 0.8)'};
    background: ${props => props.active ? 'rgba(74, 222, 128, 0.15)' : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${props => props.active ? 'rgba(74, 222, 128, 0.4)' : 'rgba(255, 255, 255, 0.12)'};
  }
  
  @media (max-width: 640px) {
    font-size: 11px;
    padding: 6px 10px;
  }
`;

const UserList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const UserItem = styled.li`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  
  @media (max-width: 640px) {
    padding: 10px 12px;
  }
`;

const UserEmail = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  word-break: break-all;
  color: white;
`;

const UserMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const UserActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
  
  @media (max-width: 640px) {
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const ActionButton = styled(Button)`
  padding: 6px 12px;
  font-size: 11px;
  border-radius: 8px;
  
  @media (max-width: 640px) {
    flex: 1 0 45%;
    margin-top: 6px;
    padding: 8px;
    text-align: center;
  }
`;

const ApproveButton = styled(ActionButton)`
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
  border-color: rgba(74, 222, 128, 0.25);
  
  &:hover {
    background: rgba(74, 222, 128, 0.25);
    border-color: rgba(74, 222, 128, 0.4);
  }
`;

const DenyButton = styled(ActionButton)`
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
  border-color: rgba(255, 107, 107, 0.2);
  
  &:hover {
    background: rgba(255, 107, 107, 0.22);
    border-color: rgba(255, 107, 107, 0.35);
  }
`;

const ToggleButton = styled(ActionButton)<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(74, 222, 128, 0.15)' : 'rgba(255, 255, 255, 0.06)'};
  color: ${props => props.active ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'};
  border-color: ${props => props.active ? 'rgba(74, 222, 128, 0.25)' : 'rgba(255, 255, 255, 0.08)'};
  
  &:hover {
    background: ${props => props.active ? 'rgba(74, 222, 128, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.active ? 'rgba(74, 222, 128, 0.4)' : 'rgba(255, 255, 255, 0.15)'};
  }
`;

const Message = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 10px 14px;
  margin-bottom: 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${props => 
    props.type === 'success' ? 'rgba(74, 222, 128, 0.2)' : 
    props.type === 'error' ? 'rgba(248, 113, 113, 0.2)' : 'rgba(148, 163, 184, 0.15)'};
  background: ${props => 
    props.type === 'success' ? 'rgba(74, 222, 128, 0.08)' : 
    props.type === 'error' ? 'rgba(248, 113, 113, 0.08)' : 'rgba(148, 163, 184, 0.06)'};
  color: ${props => 
    props.type === 'success' ? '#4ade80' : 
    props.type === 'error' ? '#f87171' : '#94a3b8'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  font-style: normal;
`;

const StatusIndicator = styled.div<{ status: 'unknown' | 'connected' | 'error' }>`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid ${props => 
    props.status === 'connected' ? 'rgba(74, 222, 128, 0.2)' : 
    props.status === 'error' ? 'rgba(248, 113, 113, 0.2)' : 'rgba(148, 163, 184, 0.15)'};
  background: ${props => 
    props.status === 'connected' ? 'rgba(74, 222, 128, 0.1)' : 
    props.status === 'error' ? 'rgba(248, 113, 113, 0.1)' : 'rgba(148, 163, 184, 0.06)'};
  color: ${props => 
    props.status === 'connected' ? '#4ade80' : 
    props.status === 'error' ? '#f87171' : '#94a3b8'};
    
  @media (max-width: 640px) {
    font-size: 9px;
    padding: 2px 6px;
    display: block;
    margin-top: 4px;
    margin-left: 0;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  margin-bottom: 10px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: white;
  font-size: 13px;
  margin-bottom: 0;
  box-sizing: border-box;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: rgba(74, 222, 128, 0.4);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.08);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
    font-size: 12px;
  }
  
  @media (max-width: 640px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const PageInfo = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  
  @media (max-width: 640px) {
    order: -1;
    margin-bottom: 4px;
  }
`;

const PageButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ResultsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const styles = {
  mobileDeleteButton: {
    flex: '1 0 100%',
    order: 4
  },
  mobileConfirmationDialog: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(20, 20, 20, 0.95)',
    backdropFilter: 'blur(20px)',
    padding: '20px',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    zIndex: 1100,
    width: '90%',
    maxWidth: '350px',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)'
  }
};

const PendingText = styled.span`
  color: #ff6b6b;
  font-weight: 700;
  font-size: 10px;
  position: relative;
  padding-left: 10px;
  display: inline-block;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    background-color: #ff6b6b;
    border-radius: 50%;
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: translateY(-50%) scale(0.8);
      opacity: 0.8;
    }
    50% {
      transform: translateY(-50%) scale(1.2);
      opacity: 1;
    }
    100% {
      transform: translateY(-50%) scale(0.8);
      opacity: 0.8;
    }
  }
`;

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose, sites, onUpdateSites }) => {
  const { user } = useAuth();
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'users' | 'sites' | 'requests' | 'sb_requests' | 'fibre_requests' | 'spt_requests'>('stats');
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [pendingUserCount, setPendingUserCount] = useState(0);
  const [databaseStatus, setDatabaseStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  
  // Site management state
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  
  // Change requests state
  const [changeRequests, setChangeRequests] = useState<SiteChangeRequest[]>([]);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [requestFilter, setRequestFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  
  // Signal box change requests state
  const [signalBoxRequests, setSignalBoxRequests] = useState<SignalBoxChangeRequest[]>([]);
  const [sbRequestFilter, setSbRequestFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  
  // Signal box management state
  const [signalBoxes, setSignalBoxes] = useState<{ id: number; name: string; internal_number: string | null; external_number: string | null }[]>([]);
  const [sbSearchQuery, setSbSearchQuery] = useState('');
  const [sbView, setSbView] = useState<'requests' | 'manage'>('requests');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  
  // Fibre change requests state
  const [fibreRequests, setFibreRequests] = useState<FibreChangeRequest[]>([]);
  const [fibreRequestFilter, setFibreRequestFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  // SPT circuit change requests state
  const [sptCircuitRequests, setSptCircuitRequests] = useState<SptCircuitChangeRequest[]>([]);
  const [sptRequestFilter, setSptRequestFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  // Search and pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const usersPerPage = 10;
  
  // Draggable panel state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth <= 640 ? 0 : 20, y: window.innerWidth <= 640 ? 0 : 20 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Tabs drag-to-scroll
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabsDrag = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false });

  const handleTabsPointerDown = useCallback((e: React.PointerEvent) => {
    const el = tabsRef.current;
    if (!el) return;
    tabsDrag.current = { isDown: true, startX: e.clientX, scrollLeft: el.scrollLeft, moved: false };
    el.setPointerCapture(e.pointerId);
    el.style.cursor = 'grabbing';
  }, []);

  const handleTabsPointerMove = useCallback((e: React.PointerEvent) => {
    if (!tabsDrag.current.isDown) return;
    const dx = e.clientX - tabsDrag.current.startX;
    if (Math.abs(dx) > 3) tabsDrag.current.moved = true;
    if (tabsRef.current) {
      tabsRef.current.scrollLeft = tabsDrag.current.scrollLeft - dx;
    }
  }, []);

  const handleTabsPointerUp = useCallback((e: React.PointerEvent) => {
    tabsDrag.current.isDown = false;
    if (tabsRef.current) {
      tabsRef.current.releasePointerCapture(e.pointerId);
      tabsRef.current.style.cursor = 'grab';
    }
  }, []);

  // Add a state for mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  
  // Add resize listener to detect mobile
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 640;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  // Adjust position when mobile state changes
  useEffect(() => {
    if (isMobile) {
      // Center modal on mobile
      setPosition({ x: 0, y: 0 });
      
      // Add body class to prevent scrolling behind modal on mobile
      document.body.classList.add('admin-panel-open');
    } else if (position.x === 0 && position.y === 0) {
      // If we're coming from mobile to desktop, set a default position
      setPosition({ x: 20, y: 20 });
      document.body.classList.remove('admin-panel-open');
    }
    
    return () => {
      document.body.classList.remove('admin-panel-open');
    };
  }, [isMobile, position.x, position.y]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Disable dragging on mobile
    if (window.innerWidth <= 640) {
      return;
    }
    
    if (e.target instanceof HTMLElement && 
        (e.target.closest('button') || 
         e.target.closest('input') || 
         e.target.closest('.user-actions'))) {
      return; // Don't start drag on buttons, inputs or action areas
    }
    
    setIsDragging(true);
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(userProfiles);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = userProfiles.filter(profile => 
        profile.email.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
    setCurrentPage(1); // Reset to first page on search
  }, [searchQuery, userProfiles]);

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Handle page change
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Filter sites based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredSites(sites);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = sites.filter(site => 
        site.name.stationName.toLowerCase().includes(query) ||
        site.name.siteCode.toLowerCase().includes(query) ||
        site.name.elrCode.toLowerCase().includes(query)
      );
      setFilteredSites(filtered);
    }
  }, [searchQuery, sites]);

  const handleSiteEdit = (site: Site) => {
    setEditingSite(site);
  };

  const handleSiteSave = async (updatedSite: Site) => {
    try {
      setLoading(true);
      
      // Update the site in Supabase database (now updates across all tables)
      await updateSiteInDatabase(updatedSite);
      
      // If successful, update the local state
      const updatedSites = sites.map(site => 
        site.name.siteCode === updatedSite.name.siteCode ? updatedSite : site
      );
      
      // Update sites in the parent component
      onUpdateSites(updatedSites, true);
      
      // Close the edit form
      setEditingSite(null);
      
      // Show success message
      setMessage({ 
        text: 'Site updated successfully. Changes will be visible when you reload the site map.', 
        type: 'success' 
      });
      
      // Force a refresh of sites if this is a site currently being viewed
      window.dispatchEvent(new CustomEvent('site-updated', { 
        detail: { siteCode: updatedSite.name.siteCode } 
      }));
      
      // Force a cache-breaking reload for the entire app
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Error updating site:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Failed to update site', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSiteCancel = () => {
    setEditingSite(null);
  };

  // Test database connection
  const testDatabaseConnection = async () => {
    try {
      setLoading(true);
      setMessage({ text: 'Testing database connection...', type: 'info' });
      
      // Check if we can connect to Supabase and query the users table
      const { error } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact' });
      
      if (error) {
        console.error('Database connection test failed:', error);
        setDatabaseStatus('error');
        setMessage({ 
          text: `Database connection error: ${error.message}. Check console for details.`, 
          type: 'error' 
        });
        return;
      }
      
      setDatabaseStatus('connected');
      setMessage({ text: 'Successfully connected to database', type: 'success' });
      console.log('Database connection successful, table exists');
      
      // Once we confirm connection works, fetch users
      fetchUsers();
    } catch (error) {
      console.error('Error testing database connection:', error);
      setDatabaseStatus('error');
      setMessage({ 
        text: 'Failed to connect to database. Check console for details.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('AdminPanel mounted, user:', user?.email);
  }, [user]);

  useEffect(() => {
    if (activeTab === 'users') {
      console.log('Users tab activated - testing database connection');
      testDatabaseConnection();
    } else if (activeTab === 'stats') {
      fetchUserStats();
    } else if (activeTab === 'requests') {
      fetchChangeRequests();
    } else if (activeTab === 'sb_requests') {
      fetchSignalBoxRequests();
    } else if (activeTab === 'fibre_requests') {
      fetchFibreRequests();
    } else if (activeTab === 'spt_requests') {
      fetchSptCircuitRequestsList();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      setMessage(null); // Clear any previous messages
      
      console.log('Fetching user stats from Supabase...');
      
      // Check if supabase is properly initialized
      if (!supabase) {
        console.error('Supabase client not initialized');
        setMessage({ text: 'Failed to initialize database connection', type: 'error' });
        setDatabaseStatus('error');
        return;
      }
      
      // Get total user count
      const { count: totalCount, error: totalError } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact' });

      if (totalError) {
        console.error('Supabase error details:', totalError);
        setMessage({ 
          text: `Failed to load user stats: ${totalError.message || 'Unknown database error'}`, 
          type: 'error' 
        });
        setDatabaseStatus('error');
        throw totalError;
      }

      // Get pending users count
      const { count: pendingCount, error: pendingError } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact' })
        .eq('is_approved', false);

      if (pendingError) {
        console.error('Supabase error details:', pendingError);
        throw pendingError;
      }

      setUserCount(totalCount || 0);
      setPendingUserCount(pendingCount || 0);
      setDatabaseStatus('connected');
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setDatabaseStatus('error');
      setMessage({ 
        text: error instanceof Error 
          ? `Failed to load user stats: ${error.message}` 
          : 'Failed to load user stats: Unknown error', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setMessage(null); // Clear any previous messages
      
      console.log('Fetching users from Supabase...');
      
      // Check if supabase is properly initialized
      if (!supabase) {
        console.error('Supabase client not initialized');
        setMessage({ text: 'Failed to initialize database connection', type: 'error' });
        return;
      }
      
      // First try to get users from user_profiles
      const { data: userProfilesData, error: userProfilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      let profilesData: any[] = [];
      
      if (userProfilesError) {
        console.error('Error fetching from user_profiles, trying profiles:', userProfilesError);
        
        // If user_profiles fails, try the profiles table
        const { data: fetchedProfilesData, error: profilesError } = await supabase
          .from('user_profiles')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (profilesError) {
          console.error('Supabase error details:', profilesError);
          setMessage({ 
            text: `Failed to load users: ${profilesError.message || 'Unknown database error'}`, 
            type: 'error' 
          });
          throw profilesError;
        }
        
        profilesData = fetchedProfilesData || [];
        
        // Map profiles data to include approval fields if they don't exist
        const mappedProfiles = profilesData.map(profile => ({
          ...profile,
          is_admin: profile.is_admin || false,
          is_approved: profile.is_approved === undefined ? true : profile.is_approved
        }));
        
        console.log('Users fetched from profiles table:', mappedProfiles?.length || 0);
        setUserProfiles(mappedProfiles || []);
      } else {
        console.log('Users fetched from user_profiles table:', userProfilesData?.length || 0);
        setUserProfiles(userProfilesData || []);
      }
      
      // If we got to this point, but data is empty, show informative message
      if ((!userProfilesData || userProfilesData.length === 0) && (profilesData.length === 0)) {
        setMessage({ text: 'No users found in the database', type: 'info' });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ 
        text: error instanceof Error 
          ? `Failed to load users: ${error.message}` 
          : 'Failed to load users: Unknown error', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      setMessage({ text: 'Approving user...', type: 'info' });
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_approved: true })
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      setUserProfiles(userProfiles.map(profile => 
        profile.id === userId ? { ...profile, is_approved: true } : profile
      ));
      
      // Update counts
      setPendingUserCount(prev => Math.max(0, prev - 1));
      
      setMessage({ text: 'User approved successfully', type: 'success' });
    } catch (error) {
      console.error('Error approving user:', error);
      setMessage({ text: 'Failed to approve user', type: 'error' });
    }
  };

  const handleDenyUser = async (userId: string) => {
    try {
      setMessage({ text: 'Denying user...', type: 'info' });
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      setUserProfiles(userProfiles.filter(profile => profile.id !== userId));
      
      // Update counts
      setPendingUserCount(prev => Math.max(0, prev - 1));
      setUserCount(prev => Math.max(0, prev - 1));
      
      setMessage({ text: 'User denied successfully', type: 'success' });
    } catch (error) {
      console.error('Error denying user:', error);
      setMessage({ text: 'Failed to deny user', type: 'error' });
    }
  };

  const toggleUserApproval = async (user: UserProfile) => {
    try {
      const newStatus = !user.is_approved;
      setMessage({ text: `${newStatus ? 'Approving' : 'Unapproving'} user...`, type: 'info' });
      
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_approved: newStatus })
        .eq('id', user.id);

      if (error) throw error;

      // Update local state
      setUserProfiles(userProfiles.map(profile => 
        profile.id === user.id ? { ...profile, is_approved: newStatus } : profile
      ));
      
      // Update pending count
      setPendingUserCount(prev => newStatus ? Math.max(0, prev - 1) : prev + 1);
      
      setMessage({ 
        text: `User ${newStatus ? 'approved' : 'unapproved'} successfully`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error toggling user approval:', error);
      setMessage({ text: 'Failed to update user', type: 'error' });
    }
  };

  const toggleAdminStatus = async (user: UserProfile) => {
    try {
      const newStatus = !user.is_admin;
      setMessage({ text: `${newStatus ? 'Granting' : 'Revoking'} admin privileges...`, type: 'info' });
      
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_admin: newStatus })
        .eq('id', user.id);

      if (error) throw error;

      // Update local state
      setUserProfiles(userProfiles.map(profile => 
        profile.id === user.id ? { ...profile, is_admin: newStatus } : profile
      ));
      
      setMessage({ 
        text: `Admin privileges ${newStatus ? 'granted' : 'revoked'} successfully`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error toggling admin status:', error);
      setMessage({ text: 'Failed to update admin status', type: 'error' });
    }
  };

  const handleUserDelete = async (userId: string) => {
    try {
      setMessage({ text: 'Deleting user...', type: 'info' });
      
      // The actual deletion happens in the AdminUserDelete component
      // This is just to update the UI after successful deletion
      
      // Update local state
      setUserProfiles(userProfiles.filter(profile => profile.id !== userId));
      
      // Update counts
      setUserCount(prev => Math.max(0, prev - 1));
      if (!userProfiles.find(p => p.id === userId)?.is_approved) {
        setPendingUserCount(prev => Math.max(0, prev - 1));
      }
      
      setMessage({ text: 'User deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error handling user deletion:', error);
      setMessage({ text: 'Error updating UI after user deletion', type: 'error' });
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      setMessage({ text: 'Sending password reset email...', type: 'info' });
      
      // Build a proper redirect URL to our reset password page
      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Error sending password reset:', error);
        setMessage({ text: `Failed to send password reset: ${error.message}`, type: 'error' });
        return;
      }

      setMessage({ text: `Password reset email sent to ${email}`, type: 'success' });
    } catch (error) {
      console.error('Unexpected error sending password reset:', error);
      setMessage({ text: 'Failed to send password reset', type: 'error' });
    }
  };

  // Fetch change requests from the database
  const fetchChangeRequests = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const requests = await fetchSiteChangeRequests();
      console.log('Fetched change requests:', requests.length);
      setChangeRequests(requests);
      
      if (requests.length === 0) {
        setMessage({ text: 'No change requests found', type: 'info' });
      }
    } catch (error) {
      console.error('Error fetching change requests:', error);
      setMessage({ 
        text: error instanceof Error 
          ? `Failed to load change requests: ${error.message}` 
          : 'Failed to load change requests', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle approving a change request
  const handleApproveRequest = async (requestId: string, siteCode: string) => {
    try {
      setProcessingRequest(requestId);
      setMessage({ text: 'Processing approval...', type: 'info' });
      
      // Find the site in our local state
      const siteToUpdate = sites.find(site => site.name.siteCode === siteCode);
      
      if (!siteToUpdate) {
        throw new Error(`Site with code ${siteCode} not found`);
      }
      
      // Find the change request to get the suggested_changes (access info)
      const request = changeRequests.find(r => r.id === requestId);
      
      console.log('Approving request:', requestId, 'for site:', siteCode);
      
      // Update the request status to approved
      await updateSiteChangeRequestStatus(requestId, 'approved');
      
      // Apply the suggested changes as the site's access info
      if (request?.suggested_changes) {
        const updatedSite = { ...siteToUpdate, access: request.suggested_changes };
        console.log('Applying access info to site:', siteCode, '->', request.suggested_changes);
        
        try {
          await updateSiteInDatabase(updatedSite);
          
          // Update local state
          const updatedSites = sites.map(site =>
            site.name.siteCode === siteCode ? updatedSite : site
          );
          onUpdateSites(updatedSites, true);
          
          console.log('Successfully updated access info for site:', siteCode);
        } catch (updateErr) {
          console.error('Error updating site access info:', updateErr);
          // Don't throw - the approval succeeded, just the site update failed
          setMessage({ 
            text: 'Request approved but failed to update site access info. Try editing the site manually.', 
            type: 'error' 
          });
          setEditingSite(siteToUpdate);
          setActiveTab('sites');
          return;
        }
      }
      
      // Fetch updated request list
      await fetchChangeRequests();
      
      setMessage({ text: 'Change request approved and access info updated.', type: 'success' });
      
    } catch (error) {
      console.error('Error approving change request:', error);
      setMessage({ 
        text: error instanceof Error 
          ? error.message 
          : 'Failed to approve change request', 
        type: 'error' 
      });
    } finally {
      setProcessingRequest(null);
    }
  };
  
  // Handle rejecting a change request
  const handleRejectRequest = async (requestId: string) => {
    try {
      setProcessingRequest(requestId);
      setMessage({ text: 'Processing rejection...', type: 'info' });
      
      console.log('Rejecting request:', requestId);
      
      // Update the request status to rejected
      await updateSiteChangeRequestStatus(requestId, 'rejected');
      
      // Fetch updated request list
      await fetchChangeRequests();
      
      setMessage({ text: 'Change request rejected', type: 'success' });
    } catch (error) {
      console.error('Error rejecting change request:', error);
      setMessage({ 
        text: error instanceof Error 
          ? error.message 
          : 'Failed to reject change request', 
        type: 'error' 
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  // ==========================================
  // Signal Box Change Request handlers
  // ==========================================
  const fetchSignalBoxRequests = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const requests = await fetchSignalBoxChangeRequests();
      console.log('Fetched signal box change requests:', requests.length);
      setSignalBoxRequests(requests);
      
      if (requests.length === 0) {
        setMessage({ text: 'No signal box change requests found', type: 'info' });
      }
    } catch (error) {
      console.error('Error fetching signal box change requests:', error);
      setMessage({ 
        text: error instanceof Error 
          ? `Failed to load signal box change requests: ${error.message}` 
          : 'Failed to load signal box change requests', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSbRequest = async (request: SignalBoxChangeRequest) => {
    if (!request.id) return;
    try {
      setProcessingRequest(request.id);
      setMessage({ text: 'Approving and applying change...', type: 'info' });
      
      // Mark as approved
      await updateSignalBoxChangeRequestStatus(request.id, 'approved');
      
      // Apply the change to the signal_boxes table
      await applySignalBoxChange(
        request.signal_box_id,
        request.suggested_internal,
        request.suggested_external
      );
      
      // Refresh the list
      await fetchSignalBoxRequests();
      
      setMessage({ text: 'Signal box change approved and applied successfully', type: 'success' });
    } catch (error) {
      console.error('Error approving signal box change:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Failed to approve change', 
        type: 'error' 
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectSbRequest = async (requestId: string) => {
    try {
      setProcessingRequest(requestId);
      setMessage({ text: 'Rejecting change...', type: 'info' });
      
      await updateSignalBoxChangeRequestStatus(requestId, 'rejected');
      
      await fetchSignalBoxRequests();
      
      setMessage({ text: 'Signal box change request rejected', type: 'success' });
    } catch (error) {
      console.error('Error rejecting signal box change:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Failed to reject change', 
        type: 'error' 
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  // Signal box management handlers
  const fetchSignalBoxList = async () => {
    try {
      setLoading(true);
      setMessage(null);
      const boxes = await fetchAllSignalBoxes();
      setSignalBoxes(boxes);
      console.log('Fetched signal boxes for management:', boxes.length);
    } catch (error) {
      console.error('Error fetching signal boxes:', error);
      setMessage({
        text: error instanceof Error ? error.message : 'Failed to load signal boxes',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSignalBox = async (id: number, name: string) => {
    try {
      setDeletingId(id);
      setMessage({ text: `Deleting "${name}"...`, type: 'info' });

      await deleteSignalBox(id);

      // Remove from local state
      setSignalBoxes(prev => prev.filter(box => box.id !== id));
      setConfirmDeleteId(null);
      setMessage({ text: `"${name}" deleted successfully`, type: 'success' });
    } catch (error) {
      console.error('Error deleting signal box:', error);
      setMessage({
        text: error instanceof Error ? error.message : 'Failed to delete signal box',
        type: 'error'
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ==========================================
  // Fibre Cable Change Request handlers
  // ==========================================
  const fetchFibreRequests = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const requests = await fetchFibreChangeRequests();
      console.log('Fetched fibre change requests:', requests.length);
      setFibreRequests(requests);
      
      if (requests.length === 0) {
        setMessage({ text: 'No fibre change requests found', type: 'info' });
      }
    } catch (error) {
      console.error('Error fetching fibre change requests:', error);
      setMessage({ 
        text: error instanceof Error 
          ? `Failed to load fibre change requests: ${error.message}` 
          : 'Failed to load fibre change requests', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveFibreRequest = async (request: FibreChangeRequest) => {
    if (!request.id) return;
    try {
      setProcessingRequest(request.id);
      
      const isNewJoint = request.request_type === 'new' || !request.fibre_site_id;
      setMessage({ text: isNewJoint ? 'Approving and creating new fibre joint...' : 'Approving and applying fibre change...', type: 'info' });
      
      // Mark as approved
      await updateFibreChangeRequestStatus(request.id, 'approved');
      
      if (isNewJoint) {
        // Create a new row in fibre_sites
        await insertNewFibreSite(request);
      } else {
        // Apply the change to the existing fibre_sites row
        await applyFibreChange(request);
      }
      
      // Refresh the list
      await fetchFibreRequests();
      
      setMessage({ text: isNewJoint ? 'New fibre joint created successfully' : 'Fibre change approved and applied successfully', type: 'success' });
    } catch (error) {
      console.error('Error approving fibre change:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Failed to approve fibre change', 
        type: 'error' 
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectFibreRequest = async (requestId: string) => {
    try {
      setProcessingRequest(requestId);
      setMessage({ text: 'Rejecting fibre change...', type: 'info' });
      
      await updateFibreChangeRequestStatus(requestId, 'rejected');
      
      await fetchFibreRequests();
      
      setMessage({ text: 'Fibre change request rejected', type: 'success' });
    } catch (error) {
      console.error('Error rejecting fibre change:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Failed to reject fibre change', 
        type: 'error' 
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  // SPT Circuit change request handlers
  const fetchSptCircuitRequestsList = async () => {
    try {
      setLoading(true);
      const data = await fetchSptCircuitChangeRequests();
      setSptCircuitRequests(data);
    } catch (error) {
      console.error('Error fetching SPT circuit change requests:', error);
      setMessage({ text: 'Failed to load SPT circuit change requests', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSptCircuitRequest = async (request: SptCircuitChangeRequest) => {
    if (!request.id) return;
    try {
      setProcessingRequest(request.id);
      setMessage({ text: 'Approving circuit change...', type: 'info' });

      await applySptCircuitChange(request);
      await updateSptCircuitChangeRequestStatus(request.id, 'approved');
      await fetchSptCircuitRequestsList();

      setMessage({ text: 'Circuit change approved and applied successfully', type: 'success' });
    } catch (error) {
      console.error('Error approving SPT circuit change:', error);
      setMessage({
        text: error instanceof Error ? error.message : 'Failed to approve circuit change',
        type: 'error'
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectSptCircuitRequest = async (requestId: string) => {
    try {
      setProcessingRequest(requestId);
      setMessage({ text: 'Rejecting circuit change...', type: 'info' });

      await updateSptCircuitChangeRequestStatus(requestId, 'rejected');
      await fetchSptCircuitRequestsList();

      setMessage({ text: 'Circuit change request rejected', type: 'success' });
    } catch (error) {
      console.error('Error rejecting SPT circuit change:', error);
      setMessage({
        text: error instanceof Error ? error.message : 'Failed to reject circuit change',
        type: 'error'
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const filteredSignalBoxes = sbSearchQuery.trim()
    ? signalBoxes.filter(box =>
        box.name.toLowerCase().includes(sbSearchQuery.toLowerCase()) ||
        (box.internal_number && box.internal_number.includes(sbSearchQuery)) ||
        (box.external_number && box.external_number.includes(sbSearchQuery))
      )
    : signalBoxes;

  // Inside the component, add this function to handle tab icons for mobile:
  const getTabIcon = (tab: 'stats' | 'users' | 'sites' | 'requests' | 'sb_requests' | 'fibre_requests' | 'spt_requests') => {
    if (window.innerWidth <= 640) {
      if (tab === 'stats') return '📊 ';
      if (tab === 'spt_requests') return '🚦 ';
      if (tab === 'users') return '👥 ';
      if (tab === 'sites') return '🏢 ';
      if (tab === 'requests') return '📝 ';
      if (tab === 'sb_requests') return '📞 ';
      if (tab === 'fibre_requests') return '🔵 ';
    }
    return '';
  };

  // Add a style for the body when admin panel is open on mobile
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      body.admin-panel-open {
        overflow: hidden;
        position: fixed;
        width: 100%;
        height: 100%;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Compute unique site types from all sites
  const standardTypes = [
    'Station',
    'Signal Box',
    'Junction',
    'Depot',
    'Yard',
    'Other'
  ];
  const uniqueTypes = Array.from(new Set([
    ...standardTypes,
    ...sites.map(site => site.name.siteType?.trim()).filter(Boolean)
  ])).sort((a, b) => a.localeCompare(b));

  if (!user) {
    return null;
  }

  return (
    <AdminPanelContainer 
      ref={panelRef}
      isDragging={isDragging} 
      posX={position.x} 
      posY={position.y}
    >
      <PanelHeader onMouseDown={handleMouseDown}>
        <Title>
          Admin Panel
          {activeTab === 'users' && (
            <StatusIndicator status={databaseStatus}>
              {databaseStatus === 'connected' && '✓ Connected'}
              {databaseStatus === 'error' && '✗ Connection Error'}
              {databaseStatus === 'unknown' && '⟳ Checking...'}
            </StatusIndicator>
          )}
        </Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </PanelHeader>

      {message && (
        <Message type={message.type}>
          {message.text}
        </Message>
      )}

      <Tabs
        ref={tabsRef}
        onPointerDown={handleTabsPointerDown}
        onPointerMove={handleTabsPointerMove}
        onPointerUp={handleTabsPointerUp}
        onPointerLeave={handleTabsPointerUp}
      >
        <Tab 
          active={activeTab === 'stats'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('stats'); }}
        >
          {getTabIcon('stats')}Stats
        </Tab>
        <Tab 
          active={activeTab === 'users'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('users'); }}
        >
          {getTabIcon('users')}User Management
        </Tab>
        <Tab 
          active={activeTab === 'sites'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('sites'); }}
        >
          {getTabIcon('sites')}Site Management
        </Tab>
        <Tab 
          active={activeTab === 'requests'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('requests'); }}
        >
          {getTabIcon('requests')}
          {changeRequests.filter(req => req.status === 'pending').length > 0 ? (
            <PendingText>Change Requests</PendingText>
          ) : (
            'Change Requests'
          )}
        </Tab>
        <Tab 
          active={activeTab === 'sb_requests'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('sb_requests'); }}
        >
          {getTabIcon('sb_requests')}
          {signalBoxRequests.filter(req => req.status === 'pending').length > 0 ? (
            <PendingText>Signal Boxes</PendingText>
          ) : (
            'Signal Boxes'
          )}
        </Tab>
        <Tab 
          active={activeTab === 'fibre_requests'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('fibre_requests'); }}
        >
          {getTabIcon('fibre_requests')}
          {fibreRequests.filter(req => req.status === 'pending').length > 0 ? (
            <PendingText>Fibre</PendingText>
          ) : (
            'Fibre'
          )}
        </Tab>
        <Tab 
          active={activeTab === 'spt_requests'} 
          onClick={() => { if (!tabsDrag.current.moved) setActiveTab('spt_requests'); }}
        >
          {getTabIcon('spt_requests')}
          {sptCircuitRequests.filter(req => req.status === 'pending').length > 0 ? (
            <PendingText>HiPath</PendingText>
          ) : (
            'HiPath'
          )}
        </Tab>
      </Tabs>

      {activeTab === 'users' && databaseStatus === 'error' && (
        <Section>
          <Button onClick={testDatabaseConnection} disabled={loading}>
            {loading ? 'Testing...' : 'Test Database Connection'}
          </Button>
        </Section>
      )}

      {activeTab === 'stats' && (
        <Section>
          <StatCard>
            <StatLabel>Total Sites</StatLabel>
            <StatValue>{sites.length}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Registered Users</StatLabel>
            <StatValue>{userCount}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Pending Approvals</StatLabel>
            <StatValue>{pendingUserCount}</StatValue>
          </StatCard>
        </Section>
      )}

      {activeTab === 'sites' && (
        <Section>
          <SectionTitle>Site Management</SectionTitle>
          
          {/* Search input */}
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search sites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchWrapper>
          
          {editingSite ? (
            <SiteEditForm
              site={editingSite}
              onSave={handleSiteSave}
              onCancel={handleSiteCancel}
              siteTypes={uniqueTypes}
            />
          ) : (
            <SiteList>
              {filteredSites.map((site) => (
                <SiteItem key={site.name.siteCode}>
                  <SiteInfo>
                    <SiteName>{site.name.stationName}</SiteName>
                    <SiteDetails>
                      <span>Code: {site.name.siteCode}</span>
                      <span>ELR: {site.name.elrCode}</span>
                      <span>Type: {site.name.siteType}</span>
                    </SiteDetails>
                    <SiteCoordinates>
                      Lat: {site.coordinates.lat.toFixed(6)}, 
                      Lng: {site.coordinates.lng.toFixed(6)}, 
                      Alt: {site.coordinates.altitude}m
                    </SiteCoordinates>
                  </SiteInfo>
                  <SiteActions>
                    <ActionButton onClick={() => handleSiteEdit(site)}>
                      Edit
                    </ActionButton>
                  </SiteActions>
                </SiteItem>
              ))}
            </SiteList>
          )}
        </Section>
      )}

      {activeTab === 'users' && (
        <Section>
          <SectionTitle>User Management</SectionTitle>
          
          {/* Search input */}
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchWrapper>
          
          {loading ? (
            <Message type="info">Loading users...</Message>
          ) : filteredUsers.length > 0 ? (
            <>
              <UserList>
                {currentUsers.map((profile) => (
                  <UserItem key={profile.id}>
                    <UserEmail>{profile.email}</UserEmail>
                    <UserMeta>
                      <span>Created: {new Date(profile.created_at).toLocaleDateString()}</span>
                      <span>
                        Status: {profile.is_approved ? 'Approved' : 'Pending'}
                      </span>
                    </UserMeta>
                    <UserActions className="user-actions">
                      <ToggleButton
                        active={profile.is_admin}
                        onClick={() => toggleAdminStatus(profile)}
                      >
                        {profile.is_admin ? 'Admin' : 'Make Admin'}
                      </ToggleButton>
                      <ToggleButton
                        active={profile.is_approved}
                        onClick={() => toggleUserApproval(profile)}
                      >
                        {profile.is_approved ? 'Approved' : 'Approve'}
                      </ToggleButton>
                      {isMobile ? (
                        // On mobile, split into two rows
                        <>
                          <ActionButton
                            onClick={() => handlePasswordReset(profile.email)}
                            title="Send password reset email to this user"
                            style={{ flex: '1 0 100%', order: 3 }}
                          >
                            Reset Password
                          </ActionButton>
                          <AdminUserDelete 
                            userId={profile.id}
                            userEmail={profile.email}
                            onSuccess={() => handleUserDelete(profile.id)}
                            style={styles.mobileDeleteButton}
                          />
                        </>
                      ) : (
                        // On desktop, keep in one row
                        <>
                          <ActionButton
                            onClick={() => handlePasswordReset(profile.email)}
                            title="Send password reset email to this user"
                          >
                            Reset Password
                          </ActionButton>
                          <AdminUserDelete 
                            userId={profile.id}
                            userEmail={profile.email}
                            onSuccess={() => handleUserDelete(profile.id)}
                          />
                        </>
                      )}
                    </UserActions>
                  </UserItem>
                ))}
              </UserList>
              
              {/* Pagination controls */}
              {filteredUsers.length > usersPerPage && (
                <PaginationControls>
                  <PageButton 
                    onClick={prevPage}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PageButton>
                  <PageInfo>
                    Page {currentPage} of {totalPages} 
                    ({filteredUsers.length} users)
                  </PageInfo>
                  <PageButton 
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </PageButton>
                </PaginationControls>
              )}
            </>
          ) : (
            <EmptyState>
              {searchQuery ? 'No users found matching your search' : 'No users found'}
            </EmptyState>
          )}
        </Section>
      )}

      {activeTab === 'requests' && (
        <Section>
          <SectionTitle>Site Change Requests</SectionTitle>
          
          {/* Filter buttons */}
          <FilterContainer>
            <FilterButton 
              active={requestFilter === 'all'} 
              onClick={() => setRequestFilter('all')}
            >
              All ({changeRequests.length})
            </FilterButton>
            <FilterButton 
              active={requestFilter === 'pending'} 
              onClick={() => setRequestFilter('pending')}
              color="#ffc107"
            >
              Pending ({changeRequests.filter(r => r.status === 'pending').length})
            </FilterButton>
            <FilterButton 
              active={requestFilter === 'approved'} 
              onClick={() => setRequestFilter('approved')}
              color="#28a745"
            >
              Approved ({changeRequests.filter(r => r.status === 'approved').length})
            </FilterButton>
            <FilterButton 
              active={requestFilter === 'rejected'} 
              onClick={() => setRequestFilter('rejected')}
              color="#dc3545"
            >
              Rejected ({changeRequests.filter(r => r.status === 'rejected').length})
            </FilterButton>
          </FilterContainer>
          
          <SearchWrapper>
            {/* Search input */}
            <SearchInput
              type="text"
              placeholder="Search by site code or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchWrapper>
          
          {/* Debug info */}
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', margin: '0 0 10px 0' }}>
            Total requests: {changeRequests.length}, 
            Pending: {changeRequests.filter(r => r.status === 'pending').length}, 
            Processed: {changeRequests.filter(r => r.status !== 'pending').length}
          </p>
          
          {loading ? (
            <Message type="info">Loading change requests...</Message>
          ) : changeRequests.length > 0 ? (
            <RequestsList>
              {changeRequests
                .filter(request => 
                  // First filter by status
                  (requestFilter === 'all' || request.status === requestFilter) &&
                  // Then filter by search text
                  (!searchQuery.trim() || 
                  request.site_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  request.user_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (request.station_name || '').toLowerCase().includes(searchQuery.toLowerCase()))
                )
                .map((request) => (
                  <RequestItem key={request.id} status={request.status}>
                    <RequestDetails>
                      <RequestHeader>
                        <SiteName>{request.station_name || request.site_code}</SiteName>
                        <RequestStatus status={request.status}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </RequestStatus>
                      </RequestHeader>
                      <RequestMeta>
                        <span>ID: {request.id?.substring(0, 8)}...</span>
                        <span>Site Code: {request.site_code}</span>
                        <span>By: {request.user_email}</span>
                        <span>When: {request.created_at ? new Date(request.created_at).toLocaleString() : 'Unknown'}</span>
                      </RequestMeta>
                      <RequestContent>
                        {request.suggested_changes}
                      </RequestContent>
                    </RequestDetails>
                    
                    {request.status === 'pending' && (
                      <RequestActions>
                        <ApproveButton 
                          onClick={() => handleApproveRequest(request.id || '', request.site_code)}
                          disabled={!!processingRequest}
                        >
                          {processingRequest === request.id ? 'Processing...' : 'Approve'}
                        </ApproveButton>
                        <DenyButton 
                          onClick={() => handleRejectRequest(request.id || '')}
                          disabled={!!processingRequest}
                        >
                          {processingRequest === request.id ? 'Processing...' : 'Reject'}
                        </DenyButton>
                      </RequestActions>
                    )}
                  </RequestItem>
                ))}
            </RequestsList>
          ) : (
            <EmptyState>
              {searchQuery 
                ? 'No requests found matching your search' 
                : requestFilter === 'all' 
                  ? 'No change requests found'
                  : `No ${requestFilter} requests found`
              }
            </EmptyState>
          )}
        </Section>
      )}
      {activeTab === 'sb_requests' && (
        <Section>
          {/* Sub-view toggle */}
          <FilterContainer>
            <FilterButton
              active={sbView === 'requests'}
              onClick={() => setSbView('requests')}
            >
              Change Requests
            </FilterButton>
            <FilterButton
              active={sbView === 'manage'}
              onClick={() => {
                setSbView('manage');
                if (signalBoxes.length === 0) fetchSignalBoxList();
              }}
            >
              Manage Signal Boxes
            </FilterButton>
          </FilterContainer>

          {/* Change Requests sub-view */}
          {sbView === 'requests' && (
            <>
              <SectionTitle>Signal Box Change Requests</SectionTitle>
              
              <FilterContainer>
                <FilterButton 
                  active={sbRequestFilter === 'all'} 
                  onClick={() => setSbRequestFilter('all')}
                >
                  All ({signalBoxRequests.length})
                </FilterButton>
                <FilterButton 
                  active={sbRequestFilter === 'pending'} 
                  onClick={() => setSbRequestFilter('pending')}
                  color="#ffc107"
                >
                  Pending ({signalBoxRequests.filter(r => r.status === 'pending').length})
                </FilterButton>
                <FilterButton 
                  active={sbRequestFilter === 'approved'} 
                  onClick={() => setSbRequestFilter('approved')}
                  color="#28a745"
                >
                  Approved ({signalBoxRequests.filter(r => r.status === 'approved').length})
                </FilterButton>
                <FilterButton 
                  active={sbRequestFilter === 'rejected'} 
                  onClick={() => setSbRequestFilter('rejected')}
                  color="#dc3545"
                >
                  Rejected ({signalBoxRequests.filter(r => r.status === 'rejected').length})
                </FilterButton>
              </FilterContainer>
              
              {loading ? (
                <Message type="info">Loading signal box change requests...</Message>
              ) : signalBoxRequests.length > 0 ? (
                <RequestsList>
                  {signalBoxRequests
                    .filter(request => 
                      sbRequestFilter === 'all' || request.status === sbRequestFilter
                    )
                    .map((request) => (
                      <RequestItem key={request.id} status={request.status}>
                        <RequestDetails>
                          <RequestHeader>
                            <SiteName>{request.signal_box_name}</SiteName>
                            <RequestStatus status={request.status}>
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </RequestStatus>
                          </RequestHeader>
                          <RequestMeta>
                            <span>By: {request.user_email}</span>
                            <span>When: {request.created_at ? new Date(request.created_at).toLocaleString() : 'Unknown'}</span>
                          </RequestMeta>
                          <RequestContent>
                            {request.suggested_internal && (
                              <div>Internal: <strong>{request.suggested_internal}</strong></div>
                            )}
                            {request.suggested_external && (
                              <div>External: <strong>{request.suggested_external}</strong></div>
                            )}
                            {request.notes && (
                              <div style={{ marginTop: 6, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                                Notes: {request.notes}
                              </div>
                            )}
                          </RequestContent>
                        </RequestDetails>
                        
                        {request.status === 'pending' && (
                          <RequestActions>
                            <ApproveButton 
                              onClick={() => handleApproveSbRequest(request)}
                              disabled={!!processingRequest}
                            >
                              {processingRequest === request.id ? 'Processing...' : 'Approve'}
                            </ApproveButton>
                            <DenyButton 
                              onClick={() => handleRejectSbRequest(request.id || '')}
                              disabled={!!processingRequest}
                            >
                              {processingRequest === request.id ? 'Processing...' : 'Reject'}
                            </DenyButton>
                          </RequestActions>
                        )}
                      </RequestItem>
                    ))}
                </RequestsList>
              ) : (
                <EmptyState>
                  {sbRequestFilter === 'all' 
                    ? 'No signal box change requests found'
                    : `No ${sbRequestFilter} signal box requests found`
                  }
                </EmptyState>
              )}
            </>
          )}

          {/* Manage Signal Boxes sub-view */}
          {sbView === 'manage' && (
            <>
              <SectionTitle>Manage Signal Boxes ({signalBoxes.length})</SectionTitle>
              
              <SearchWrapper>
                <SearchInput
                  type="text"
                  placeholder="Search signal boxes..."
                  value={sbSearchQuery}
                  onChange={(e) => setSbSearchQuery(e.target.value)}
                />
              </SearchWrapper>

              {loading ? (
                <Message type="info">Loading signal boxes...</Message>
              ) : filteredSignalBoxes.length > 0 ? (
                <SbManageList>
                  {filteredSignalBoxes.map((box) => (
                    <SbManageItem key={box.id}>
                      <SbManageInfo>
                        <SbManageName>{box.name}</SbManageName>
                        <SbManageNumbers>
                          <span>Int: {box.internal_number || 'N/A'}</span>
                          <span>Ext: {box.external_number || 'N/A'}</span>
                        </SbManageNumbers>
                      </SbManageInfo>
                      <SbManageActions>
                        {confirmDeleteId === box.id ? (
                          <>
                            <SbConfirmText>Delete?</SbConfirmText>
                            <ApproveButton
                              onClick={() => handleDeleteSignalBox(box.id, box.name)}
                              disabled={deletingId === box.id}
                              style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem' }}
                            >
                              {deletingId === box.id ? '...' : 'Yes'}
                            </ApproveButton>
                            <ActionButton
                              onClick={() => setConfirmDeleteId(null)}
                              style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem' }}
                            >
                              No
                            </ActionButton>
                          </>
                        ) : (
                          <DeleteButton onClick={() => setConfirmDeleteId(box.id)}>
                            Delete
                          </DeleteButton>
                        )}
                      </SbManageActions>
                    </SbManageItem>
                  ))}
                </SbManageList>
              ) : (
                <EmptyState>
                  {sbSearchQuery ? 'No signal boxes matching your search' : 'No signal boxes found'}
                </EmptyState>
              )}
            </>
          )}
        </Section>
      )}
      {activeTab === 'fibre_requests' && (
        <Section>
          <SectionTitle>Fibre Cable Change Requests</SectionTitle>
          
          <FilterContainer>
            <FilterButton 
              active={fibreRequestFilter === 'all'} 
              onClick={() => setFibreRequestFilter('all')}
            >
              All ({fibreRequests.length})
            </FilterButton>
            <FilterButton 
              active={fibreRequestFilter === 'pending'} 
              onClick={() => setFibreRequestFilter('pending')}
              color="#ffc107"
            >
              Pending ({fibreRequests.filter(r => r.status === 'pending').length})
            </FilterButton>
            <FilterButton 
              active={fibreRequestFilter === 'approved'} 
              onClick={() => setFibreRequestFilter('approved')}
              color="#28a745"
            >
              Approved ({fibreRequests.filter(r => r.status === 'approved').length})
            </FilterButton>
            <FilterButton 
              active={fibreRequestFilter === 'rejected'} 
              onClick={() => setFibreRequestFilter('rejected')}
              color="#dc3545"
            >
              Rejected ({fibreRequests.filter(r => r.status === 'rejected').length})
            </FilterButton>
          </FilterContainer>
          
          {loading ? (
            <Message type="info">Loading fibre change requests...</Message>
          ) : fibreRequests.length > 0 ? (
            <RequestsList>
              {fibreRequests
                .filter(request => 
                  fibreRequestFilter === 'all' || request.status === fibreRequestFilter
                )
                .map((request) => (
                  <RequestItem key={request.id} status={request.status}>
                    <RequestDetails>
                      <RequestHeader>
                        <SiteName>
                          {(request.request_type === 'new' || !request.fibre_site_id) && (
                            <span style={{ background: 'rgba(0,188,212,0.2)', color: '#00e5ff', fontSize: '10px', padding: '2px 8px', borderRadius: '20px', marginRight: '6px', fontWeight: '700', border: '1px solid rgba(0,188,212,0.3)' }}>NEW</span>
                          )}
                          {request.suggested_cable_name || request.original_cable_name || 'Fibre Joint'}
                        </SiteName>
                        <RequestStatus status={request.status}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </RequestStatus>
                      </RequestHeader>
                      <RequestMeta>
                        {request.original_joint_name ? (
                          <span>Joint: {request.original_joint_name}</span>
                        ) : (
                          <span>New joint request</span>
                        )}
                        <span>By: {request.user_email}</span>
                        <span>When: {request.created_at ? new Date(request.created_at).toLocaleString() : 'Unknown'}</span>
                      </RequestMeta>
                      <RequestContent>
                        {request.suggested_joint_name && (
                          <div>Joint Name: <strong>{request.suggested_joint_name}</strong></div>
                        )}
                        {request.suggested_cable_name && (
                          <div>Cable Name: <strong>{request.suggested_cable_name}</strong></div>
                        )}
                        {request.suggested_section_number && (
                          <div>Section: <strong>{request.suggested_section_number}</strong></div>
                        )}
                        {request.suggested_elr && (
                          <div>ELR: <strong>{request.suggested_elr}</strong></div>
                        )}
                        {request.suggested_location && (
                          <div>Location: <strong>{request.suggested_location}</strong></div>
                        )}
                        {request.suggested_access && (
                          <div>Access: <strong>{request.suggested_access}</strong></div>
                        )}
                        {request.suggested_lat != null && (
                          <div>Lat: <strong>{request.suggested_lat}</strong></div>
                        )}
                        {request.suggested_lng != null && (
                          <div>Lng: <strong>{request.suggested_lng}</strong></div>
                        )}
                        {request.notes && (
                          <div style={{ marginTop: 6, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                            Notes: {request.notes}
                          </div>
                        )}
                      </RequestContent>
                    </RequestDetails>
                    
                    {request.status === 'pending' && (
                      <RequestActions>
                        <ApproveButton 
                          onClick={() => handleApproveFibreRequest(request)}
                          disabled={!!processingRequest}
                        >
                          {processingRequest === request.id ? 'Processing...' : 'Approve'}
                        </ApproveButton>
                        <DenyButton 
                          onClick={() => handleRejectFibreRequest(request.id || '')}
                          disabled={!!processingRequest}
                        >
                          {processingRequest === request.id ? 'Processing...' : 'Reject'}
                        </DenyButton>
                      </RequestActions>
                    )}
                  </RequestItem>
                ))}
            </RequestsList>
          ) : (
            <EmptyState>
              {fibreRequestFilter === 'all' 
                ? 'No fibre change requests found'
                : `No ${fibreRequestFilter} fibre requests found`
              }
            </EmptyState>
          )}
        </Section>
      )}
      {activeTab === 'spt_requests' && (
        <Section>
          <SectionTitle>HiPath Circuit Change Requests</SectionTitle>
          
          <FilterContainer>
            <FilterButton 
              active={sptRequestFilter === 'all'} 
              onClick={() => setSptRequestFilter('all')}
            >
              All ({sptCircuitRequests.length})
            </FilterButton>
            <FilterButton 
              active={sptRequestFilter === 'pending'} 
              onClick={() => setSptRequestFilter('pending')}
              color="#ffc107"
            >
              Pending ({sptCircuitRequests.filter(r => r.status === 'pending').length})
            </FilterButton>
            <FilterButton 
              active={sptRequestFilter === 'approved'} 
              onClick={() => setSptRequestFilter('approved')}
              color="#28a745"
            >
              Approved ({sptCircuitRequests.filter(r => r.status === 'approved').length})
            </FilterButton>
            <FilterButton 
              active={sptRequestFilter === 'rejected'} 
              onClick={() => setSptRequestFilter('rejected')}
              color="#dc3545"
            >
              Rejected ({sptCircuitRequests.filter(r => r.status === 'rejected').length})
            </FilterButton>
          </FilterContainer>
          
          {loading ? (
            <Message type="info">Loading HiPath change requests...</Message>
          ) : sptCircuitRequests.length > 0 ? (
            <RequestsList>
              {sptCircuitRequests
                .filter(request => 
                  sptRequestFilter === 'all' || request.status === sptRequestFilter
                )
                .map((request) => (
                  <RequestItem key={request.id} status={request.status}>
                    <RequestDetails>
                      <RequestHeader>
                        <SiteName>{request.circuit_name}</SiteName>
                        <RequestStatus status={request.status}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </RequestStatus>
                      </RequestHeader>
                      <RequestMeta>
                        <span>By: {request.user_email}</span>
                        <span>When: {request.created_at ? new Date(request.created_at).toLocaleString() : 'Unknown'}</span>
                      </RequestMeta>
                      <RequestContent>
                        {request.suggested_status && (
                          <div>Status: <strong style={{ color: request.suggested_status === 'faulty' ? '#ff6b6b' : request.suggested_status === 'fixed' ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>{request.suggested_status}</strong></div>
                        )}
                        {request.suggested_hipath_vert && (
                          <div>HiPath: <strong>{request.suggested_hipath_vert}{request.suggested_hipath_row ?? ''}/{request.suggested_hipath_pair ?? ''}</strong></div>
                        )}
                        {request.suggested_cable_vert && (
                          <div>Cable: <strong>{request.suggested_cable_vert}{request.suggested_cable_row ?? ''}/{request.suggested_cable_pair ?? ''}</strong></div>
                        )}
                        {request.suggested_cable_pair_no && (
                          <div>Pair No: <strong>{request.suggested_cable_pair_no}</strong></div>
                        )}
                        {request.suggested_tie_cable && (
                          <div>Tie Cable: <strong>{request.suggested_tie_cable}</strong></div>
                        )}
                        {request.suggested_notes && (
                          <div>Notes: <strong>{request.suggested_notes}</strong></div>
                        )}
                        {request.change_notes && (
                          <div style={{ marginTop: 6, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                            Reason: {request.change_notes}
                          </div>
                        )}
                      </RequestContent>
                    </RequestDetails>
                    
                    {request.status === 'pending' && (
                      <RequestActions>
                        <ApproveButton 
                          onClick={() => handleApproveSptCircuitRequest(request)}
                          disabled={!!processingRequest}
                        >
                          {processingRequest === request.id ? 'Processing...' : 'Approve'}
                        </ApproveButton>
                        <DenyButton 
                          onClick={() => handleRejectSptCircuitRequest(request.id || '')}
                          disabled={!!processingRequest}
                        >
                          {processingRequest === request.id ? 'Processing...' : 'Reject'}
                        </DenyButton>
                      </RequestActions>
                    )}
                  </RequestItem>
                ))}
            </RequestsList>
          ) : (
            <EmptyState>
              {sptRequestFilter === 'all' 
                ? 'No HiPath change requests found'
                : `No ${sptRequestFilter} HiPath requests found`
              }
            </EmptyState>
          )}
        </Section>
      )}
    </AdminPanelContainer>
  );
};

const SiteList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SiteItem = styled.li`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const SiteInfo = styled.div`
  flex: 1;
`;

const SiteName = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  color: white;
`;

const SiteDetails = styled.div`
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 4px;
`;

const SiteCoordinates = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  font-family: monospace;
`;

const SiteActions = styled.div`
  display: flex;
  gap: 6px;
`;

// Site edit form component
const SiteEditForm: React.FC<{
  site: Site;
  onSave: (site: Site) => void;
  onCancel: () => void;
  siteTypes: string[];
}> = ({ site, onSave, onCancel, siteTypes }) => {
  const [editedSite, setEditedSite] = useState<Site>({ ...site });
  const [diagnosisResult, setDiagnosisResult] = useState<null | Record<string, number>>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editedSite);
  };

  const handleDiagnoseSite = async () => {
    try {
      setDiagnosing(true);
      const result = await checkSiteInAllTables(editedSite.name.siteCode);
      setDiagnosisResult(result);
    } catch (error) {
      console.error('Error diagnosing site:', error);
    } finally {
      setDiagnosing(false);
    }
  };

  return (
    <EditForm onSubmit={handleSubmit}>
      <FormGroup>
        <Label>Station Name</Label>
        <Input
          type="text"
          value={editedSite.name.stationName}
          onChange={(e) => setEditedSite({
            ...editedSite,
            name: { ...editedSite.name, stationName: e.target.value }
          })}
        />
      </FormGroup>

      <FormGroup>
        <Label>Site Code</Label>
        <Input
          type="text"
          value={editedSite.name.siteCode}
          onChange={(e) => setEditedSite({
            ...editedSite,
            name: { ...editedSite.name, siteCode: e.target.value }
          })}
        />
      </FormGroup>

      <FormGroup>
        <Label>ELR Code</Label>
        <Input
          type="text"
          value={editedSite.name.elrCode}
          onChange={(e) => setEditedSite({
            ...editedSite,
            name: { ...editedSite.name, elrCode: e.target.value }
          })}
        />
      </FormGroup>

      <FormGroup>
        <Label>Site Type</Label>
        <Select
          value={editedSite.name.siteType}
          onChange={(e) => setEditedSite({
            ...editedSite,
            name: { ...editedSite.name, siteType: e.target.value }
          })}
        >
          {siteTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Latitude</Label>
        <Input
          type="number"
          step="0.000001"
          value={editedSite.coordinates.lat}
          onChange={(e) => setEditedSite({
            ...editedSite,
            coordinates: { ...editedSite.coordinates, lat: parseFloat(e.target.value) }
          })}
        />
      </FormGroup>

      <FormGroup>
        <Label>Longitude</Label>
        <Input
          type="number"
          step="0.000001"
          value={editedSite.coordinates.lng}
          onChange={(e) => setEditedSite({
            ...editedSite,
            coordinates: { ...editedSite.coordinates, lng: parseFloat(e.target.value) }
          })}
        />
      </FormGroup>

      <FormGroup>
        <Label>Access</Label>
        <Select
          value={editedSite.access || ''}
          onChange={(e) => setEditedSite({
            ...editedSite,
            access: e.target.value
          })}
        >
          <option value="">Not specified</option>
          <option value="No SWP Required">No SWP Required</option>
          <option value="Seperated SWP Required">Seperated SWP Required</option>
          <option value="LB Required">LB Required</option>
        </Select>
      </FormGroup>

      {diagnosisResult && (
        <DiagnosisResultBox>
          <h4>Site Diagnosis: {editedSite.name.siteCode}</h4>
          <p>This site exists in the following tables:</p>
          <ul>
            {Object.entries(diagnosisResult).map(([table, count]) => (
              <li key={table}>
                {table}: {count === -1 ? 'Error checking' : count > 0 ? `${count} entries` : 'No entries'}
              </li>
            ))}
          </ul>
          <p><small>Updates will apply to all tables where this site exists</small></p>
        </DiagnosisResultBox>
      )}

      <FormActions>
        <ActionButton type="submit">Save Changes</ActionButton>
        <CancelButton type="button" onClick={onCancel}>Cancel</CancelButton>
        <DiagnoseButton 
          type="button" 
          onClick={handleDiagnoseSite}
          disabled={diagnosing}
        >
          {diagnosing ? 'Checking...' : 'Diagnose Site'}
        </DiagnoseButton>
      </FormActions>
    </EditForm>
  );
};

// Additional styled components for the edit form
const EditForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 13px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: rgba(74, 222, 128, 0.4);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.08);
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 13px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: rgba(74, 222, 128, 0.4);
    background: rgba(255, 255, 255, 0.06);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;

const CoordinateInputs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const RequestsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const RequestItem = styled.li<{ status: string }>`
  background: ${props => 
    props.status === 'approved' ? 'rgba(74, 222, 128, 0.06)' : 
    props.status === 'rejected' ? 'rgba(248, 113, 113, 0.06)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${props => 
    props.status === 'approved' ? 'rgba(74, 222, 128, 0.15)' : 
    props.status === 'rejected' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
  transition: background 0.2s;

  &:hover {
    background: ${props => 
      props.status === 'approved' ? 'rgba(74, 222, 128, 0.08)' : 
      props.status === 'rejected' ? 'rgba(248, 113, 113, 0.08)' : 'rgba(255, 255, 255, 0.06)'};
  }
`;

const RequestDetails = styled.div`
  flex: 1;
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const RequestStatus = styled.span<{ status: string }>`
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${props => 
    props.status === 'approved' ? 'rgba(74, 222, 128, 0.15)' : 
    props.status === 'rejected' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(250, 204, 21, 0.15)'};
  color: ${props => 
    props.status === 'approved' ? '#4ade80' : 
    props.status === 'rejected' ? '#f87171' : '#facc15'};
  border: 1px solid ${props => 
    props.status === 'approved' ? 'rgba(74, 222, 128, 0.25)' : 
    props.status === 'rejected' ? 'rgba(248, 113, 113, 0.25)' : 'rgba(250, 204, 21, 0.25)'};
`;

const RequestMeta = styled.div`
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const RequestContent = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  padding: 10px 12px;
  border-radius: 8px;
  margin-top: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  white-space: pre-wrap;
  word-break: break-word;
`;

const RequestActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: flex-end;
`;

const RequestBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 107, 107, 0.2);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  margin-left: 6px;
  font-weight: 700;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 640px) {
    gap: 4px;
  }
`;

const FilterButton = styled.button<{ active: boolean; color?: string }>`
  background: ${props => props.active 
    ? `${props.color || 'rgba(74, 222, 128, 0.15)'}` 
    : 'rgba(255, 255, 255, 0.04)'};
  color: ${props => props.active 
    ? (props.color ? 'white' : '#4ade80')
    : 'rgba(255, 255, 255, 0.5)'};
  border: 1px solid ${props => props.active 
    ? (props.color ? `${props.color}80` : 'rgba(74, 222, 128, 0.3)')
    : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 20px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active 
      ? (props.color ? props.color : 'rgba(74, 222, 128, 0.2)')
      : 'rgba(255, 255, 255, 0.08)'};
  }
  
  @media (max-width: 640px) {
    padding: 5px 10px;
    font-size: 10px;
  }
`;

const DiagnosisResultBox = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px;
  margin-top: 12px;
`;

const DiagnoseButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.02);
    color: rgba(255, 255, 255, 0.25);
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.2);
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 107, 107, 0.22);
    border-color: rgba(255, 107, 107, 0.35);
  }
`;

const SbManageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SbManageItem = styled.li`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const SbManageInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SbManageName = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: white;
`;

const SbManageNumbers = styled.div`
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
`;

const SbManageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const SbConfirmText = styled.span`
  color: #ff6b6b;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
`;

const DeleteButton = styled.button`
  background: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.2);
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 107, 107, 0.2);
    border-color: rgba(255, 107, 107, 0.35);
    color: #fff;
  }
`;

export default AdminPanel; 